import { Component, LLMProvider, T_RESPONSE, CollectionRegistry, Tool, DashCompModelConfig } from "../types";
import { logger } from "../utils/logger";
import { LLM } from "../llm";
import { promptLoader } from "../userResponse/prompt-loader";
import { schema } from "../userResponse/schema";
import { userPromptErrorLogger } from "../utils/user-prompt-error-logger";
import {
	getApiKeyAndModel,
	formatComponentsForPrompt,
	formatToolsForPrompt,
	formatExistingComponentsForPrompt,
	loadDatabaseRulesForTools,
	RESOLVE_QUERY_ID_TOOL,
	resolveQueryIdsTool
} from "./utils";
import { getCurrentDateTimeForPrompt } from "../utils/datetime";
import { queryCache } from "../utils/query-cache";
import { buildDirectToolCacheKey } from "../utils/cache-key";
import { ensureQueryLimit, getDbTypeFromToolId } from "../userResponse/utils";
import { DEFAULT_QUERY_LIMIT, MAX_COMPONENT_QUERY_LIMIT, KNOWLEDGE_BASE_TOP_K } from "../userResponse/constants";
import KB from "../userResponse/knowledge-base";
import { extractPromptText } from "../userResponse/prompt-extractor";

/**
 * Create filter component and update existing components using LLM
 */
export async function createFilterWithLLM(
	prompt: string,
	components: Component[],
	existingComponents: Component[],
	anthropicApiKey?: string,
	groqApiKey?: string,
	geminiApiKey?: string,
	openaiApiKey?: string,
	llmProviders?: LLMProvider[],
	tools?: Tool[],
	dashCompModels?: DashCompModelConfig,
	collections?: CollectionRegistry,
	userId?: string
): Promise<T_RESPONSE> {
	const errors: string[] = [];

	try {
		// Filter to only include filter components (type starts with "Filter")
		const filterComponents = components.filter(c => c.type.startsWith('Filter'));

		if (filterComponents.length === 0) {
			errors.push('No filter components available');
			return { success: false, errors };
		}

		logger.debug(`[DASH_COMP_REQ:FILTER] Found ${filterComponents.length} filter components`);

		// Check if any tool is a database source tool (has 'sql' parameter)
		const hasDatabaseSourceTool = tools?.some(tool => {
			const params = tool.params || {};
			return 'sql' in params || Object.keys(params).some(key => key.toLowerCase() === 'sql');
		}) ?? false;

		// Get database schema documentation - skip if database source tools are available
		let schemaDoc: string;
		if (hasDatabaseSourceTool) {
			schemaDoc = 'Schema is available in the database source tool descriptions.';
		} else {
			schemaDoc = schema.generateSchemaDocumentation();
		}
		const databaseRules = await loadDatabaseRulesForTools(tools);

		// Knowledge base context
		let globalKnowledgeBase = 'No global knowledge base available.';
		let knowledgeBaseContext = 'No additional knowledge base context available.';
		if (collections) {
			const kbResult = await KB.getAllKnowledgeBase({
				prompt,
				collections,
				userId,
				topK: KNOWLEDGE_BASE_TOP_K,
			});
			globalKnowledgeBase = kbResult.globalContext || globalKnowledgeBase;
			const dynamicParts: string[] = [];
			if (kbResult.userContext) {
				dynamicParts.push('## User-Specific Knowledge Base\n' + kbResult.userContext);
			}
			if (kbResult.queryContext) {
				dynamicParts.push('## Relevant Knowledge Base (Query-Matched)\n' + kbResult.queryContext);
			}
			knowledgeBaseContext = dynamicParts.join('\n\n') || knowledgeBaseContext;
		}

		// Load filter-specific prompt
		const prompts = await promptLoader.loadPrompts('dash-filter-picker', {
			USER_PROMPT: prompt,
			AVAILABLE_COMPONENTS: formatComponentsForPrompt(filterComponents),
			EXISTING_COMPONENTS: formatExistingComponentsForPrompt(existingComponents),
			SCHEMA_DOC: schemaDoc || 'No database schema available',
			DATABASE_RULES: databaseRules,
			AVAILABLE_TOOLS: formatToolsForPrompt(tools),
			CURRENT_DATETIME: getCurrentDateTimeForPrompt(),
			GLOBAL_KNOWLEDGE_BASE: globalKnowledgeBase,
			KNOWLEDGE_BASE_CONTEXT: knowledgeBaseContext,
		});

		logger.logLLMPrompt('dashFilterPicker', 'system', extractPromptText(prompts.system));
		logger.logLLMPrompt('dashFilterPicker', 'user', prompts.user);

		logger.debug('[DASH_COMP_REQ:FILTER] Loaded dash-filter-picker prompts');

		const { apiKey, model } = getApiKeyAndModel(
			anthropicApiKey, groqApiKey, geminiApiKey, openaiApiKey, llmProviders, dashCompModels
		);

		if (!apiKey || !model) {
			errors.push('No API key available for any LLM provider');
			return { success: false, errors };
		}

		logger.info(`[DASH_COMP_REQ:FILTER] Using model: ${model}`);

		// Track executed tools for response
		const executedTools: Array<{ id: string; name: string; params: any; result: any; outputSchema?: any }> = [];

		// Build LLM tool definitions from external tools
		const llmTools = (tools || []).map(tool => {
			const properties: Record<string, any> = {};
			const required: string[] = [];

			Object.entries(tool.params || {}).forEach(([key, typeOrValue]) => {
				const valueStr = String(typeOrValue).toLowerCase();

				let schemaType = 'string';
				const typeMatch = valueStr.match(/^(string|number|integer|boolean|array|object)\b/);
				if (typeMatch) {
					schemaType = typeMatch[1];
				}

				const isOptional = valueStr.includes('(optional)') || valueStr.includes('optional');
				const description = typeof typeOrValue === 'string' ? typeOrValue : `Parameter: ${key}`;

				if (schemaType === 'array') {
					properties[key] = { type: 'array', items: { type: 'string' }, description };
				} else if (schemaType === 'object') {
					properties[key] = { type: 'object', description };
				} else {
					properties[key] = { type: schemaType, description };
				}

				if (!isOptional) {
					required.push(key);
				}
			});

			return {
				name: tool.id,
				description: tool.description,
				input_schema: {
					type: 'object',
					properties,
					required: required.length > 0 ? required : undefined,
				},
			};
		});
		llmTools.push(RESOLVE_QUERY_ID_TOOL);

		// Tool handler to execute external tools
		const toolHandler = async (toolName: string, toolInput: any): Promise<string> => {
			if (toolName === RESOLVE_QUERY_ID_TOOL.name) {
				logger.info(`[DASH_COMP_REQ:FILTER] Resolving ${toolInput?.queryIds?.length ?? 0} queryId token(s)`);
				return resolveQueryIdsTool(toolInput?.queryIds, collections);
			}

			const tool = tools?.find(t => t.id === toolName);
			if (!tool) {
				throw new Error(`Unknown tool: ${toolName}`);
			}

			logger.info(`[DASH_COMP_REQ:FILTER] Executing external tool: ${tool.name} (${tool.id})`);
			const result = await tool.fn(toolInput);

			executedTools.push({
				id: tool.id,
				name: tool.name,
				params: toolInput,
				result: result,
				outputSchema: tool.outputSchema
			});

			logger.info(`[DASH_COMP_REQ:FILTER] Tool ${tool.name} executed successfully`);
			return JSON.stringify(result, null, 2);
		};

		// Make LLM call with tool support
		const rawResult = await LLM.streamWithTools(
			{ sys: prompts.system, user: prompts.user },
			llmTools,
			toolHandler,
			{ model, maxTokens: 16384, temperature: 0.2, apiKey },
			5 // max iterations
		);

		// Parse the JSON response — strip markdown fences, try-catch like pick-component.ts
		const cleaned = rawResult.replace(/```(?:json)?\s*/g, '').replace(/```\s*/g, '');
		const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
		const result = jsonMatch ? (() => { try { return JSON.parse(jsonMatch[0]); } catch { return null; } })() : null;

		if (!result) {
			errors.push('Failed to parse LLM response as JSON');
			errors.push(`LLM Response: ${rawResult}`);
			return { success: false, errors };
		}

		logger.debug('[DASH_COMP_REQ:FILTER] LLM response received');
		logger.file('[DASH_COMP_REQ:FILTER] LLM response:', JSON.stringify(result, null, 2));

		// Validate filter response structure
		if (!result.filterComponent) {
			errors.push('Invalid LLM response: missing filterComponent');
			errors.push(`LLM Response: ${rawResult}`);
			userPromptErrorLogger.logError('DASH_COMP_REQ:FILTER', 'Invalid LLM response structure', {
				prompt, result, missingFields: { filterComponent: !result.filterComponent }
			});
			return { success: false, errors };
		}

		logger.info(`[DASH_COMP_REQ:FILTER] Successfully created filter: ${result.filterComponent.componentType}`);
		console.log(`[DASH_COMP_REQ:FILTER] Filter component props:`, JSON.stringify(result.filterComponent.props, null, 2));
		logger.info(`[DASH_COMP_REQ:FILTER] Updated ${result.updatedComponents?.length || 0} components`);
		if (executedTools.length > 0) {
			logger.info(`[DASH_COMP_REQ:FILTER] Executed ${executedTools.length} external tool(s): ${executedTools.map(t => t.name).join(', ')}`);
		}

		// Validate the filter's own dynamic-options SQL (if any) and replace with queryId.
		// Never ship raw SQL to the frontend — if it can't validate, fail the whole
		// filter request rather than return a filter dropdown that can't populate.
		const filterExtTool = result.filterComponent?.props?.externalTool;
		if (filterExtTool?.parameters?.sql) {
			let filterSql = filterExtTool.parameters.sql;
			const filterToolId = filterExtTool.toolId;
			const filterToolName = filterExtTool.toolName;

			filterSql = ensureQueryLimit(filterSql, DEFAULT_QUERY_LIMIT, MAX_COMPONENT_QUERY_LIMIT, getDbTypeFromToolId(filterToolId));
			filterExtTool.parameters.sql = filterSql;

			// Only a CONFIRMED failure (explicit success:false/.error) blocks the request —
			// validation being unavailable or erroring transiently is "unverified," not
			// "known broken," so it still proceeds to wrap in a queryId (matching how the
			// updatedComponents loop below already treats that distinction).
			let confirmedBroken = false;
			let filterValidationError = '';
			if (collections?.['external-tools']?.['execute']) {
				try {
					const valResult = await collections['external-tools']['execute']({
						toolId: filterToolId, toolName: filterToolName, sql: filterSql, data: {},
					});
					if (valResult?.success === false || valResult?.error) {
						confirmedBroken = true;
						filterValidationError = typeof valResult?.error === 'string' ? valResult.error : JSON.stringify(valResult?.error || 'Unknown error');
						logger.warn(`[DASH_COMP_REQ:FILTER] filter's own SQL validation failed: ${filterValidationError}`);
					} else {
						const cacheKey = `${filterToolId}:${filterSql}`;
						queryCache.set(cacheKey, valResult?.data ?? valResult);
						logger.info(`[DASH_COMP_REQ:FILTER] filter's own SQL validated`);
					}
				} catch (err) {
					logger.warn(`[DASH_COMP_REQ:FILTER] filter's own SQL validation error (proceeding unverified): ${err instanceof Error ? err.message : String(err)}`);
				}
			}

			if (confirmedBroken) {
				errors.push(`Filter dropdown's query failed validation: ${filterValidationError}`);
				return {
					success: false,
					errors,
					data: { reasoning: result.reasoning || 'Filter query failed validation', rawResponse: result }
				};
			}

			// Wrap in queryId regardless of whether validation ran — SQL never reaches the
			// frontend either way. queryCache.get() picks up cached data when we did validate.
			const cacheKey = `${filterToolId}:${filterSql}`;
			const cachedData = queryCache.get(cacheKey);
			const queryId = queryCache.storeQuery(filterSql, cachedData);
			const { sql: _filterSql, ...filterRestParams } = filterExtTool.parameters;
			filterExtTool.parameters = { queryId, ...filterRestParams };
			logger.info(`[DASH_COMP_REQ:FILTER] Replaced filter's SQL with queryId: ${queryId}`);
		}

		// Validate updated component SQL and replace with queryId (like pick-component does).
		// Never ship raw SQL to the frontend — a component whose SQL fails validation is
		// dropped from the response entirely (keeping its pre-filter query) rather than
		// shipped with unresolved sql/params.
		const skippedComponentIds: string[] = [];
		let updatedComponents = result.updatedComponents || [];
		for (const comp of updatedComponents) {
			const extTool = comp.props?.externalTool;
			if (!extTool?.toolId) continue;

			// Direct tool path: validate that the LLM only added params the tool
			// declares, then pre-execute with current parameters so the first
			// render after filter creation hits cache. SQL tools fall through to
			// the existing branch below.
			const isDirectTool = !extTool?.parameters?.sql;
			if (isDirectTool) {
				const directTool = tools?.find(t => t.id === extTool.toolId);
				if (!directTool) {
					logger.warn(`[DASH_COMP_REQ:FILTER] direct tool ${extTool.toolId} not found in tool registry`);
					continue;
				}
				const declared = new Set(Object.keys(directTool.params || {}));
				const sentKeys = Object.keys(extTool.parameters || {});
				const unknown = sentKeys.filter(k => !declared.has(k));
				if (unknown.length > 0) {
					logger.warn(`[DASH_COMP_REQ:FILTER] dropping unknown params on ${extTool.toolId}: ${unknown.join(', ')}`);
					unknown.forEach(k => delete extTool.parameters[k]);
				}
				if (directTool.cache !== false) {
					try {
						const directResult = await directTool.fn(extTool.parameters || {});
						const cacheKey = buildDirectToolCacheKey(extTool.toolId, extTool.parameters);
						queryCache.set(cacheKey, { success: true, data: directResult });
						logger.info(`[DASH_COMP_REQ:FILTER] direct tool ${extTool.toolId} validated and cached for component: ${comp.id}`);
					} catch (err) {
						const errMsg = err instanceof Error ? err.message : String(err);
						logger.warn(`[DASH_COMP_REQ:FILTER] direct tool validation failed for ${extTool.toolId} on component ${comp.id}: ${errMsg}`);
					}
				}
				continue;
			}

			if (!extTool?.parameters?.sql) continue;

			let sql = extTool.parameters.sql;
			const defaultParams = extTool.parameters.params || {};
			const toolId = extTool.toolId;
			const toolName = extTool.toolName;

			// Enforce query limit
			sql = ensureQueryLimit(sql, DEFAULT_QUERY_LIMIT, MAX_COMPONENT_QUERY_LIMIT, getDbTypeFromToolId(toolId));
			extTool.parameters.sql = sql;

			// Validate SQL with default params via external-tools.execute
			if (collections?.['external-tools']?.['execute']) {
				try {
					const valResult = await collections['external-tools']['execute']({
						toolId, toolName, sql, ...defaultParams, data: {},
					});

					if (valResult?.success === false || valResult?.error) {
						const errMsg = valResult?.error || 'Unknown error';
						logger.warn(`[DASH_COMP_REQ:FILTER] SQL validation failed for component ${comp.id}: ${typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg)}`);
						// Don't fail the whole filter, and don't ship this component's raw
						// SQL either — drop it from the response so it keeps whatever
						// pre-filter query it already had (already validated when created).
						skippedComponentIds.push(comp.id);
						continue;
					}

					// Cache validated result
					const cacheKey = `${toolId}:${sql}`;
					queryCache.set(cacheKey, valResult?.data ?? valResult);
					logger.info(`[DASH_COMP_REQ:FILTER] SQL validated and cached for component: ${comp.id}`);
				} catch (err) {
					const errMsg = err instanceof Error ? err.message : String(err);
					logger.warn(`[DASH_COMP_REQ:FILTER] SQL validation error for component ${comp.id}: ${errMsg}`);
				}
			}

			// Replace SQL with queryId — SQL never reaches the frontend
			const { sql: _sql, ...restParams } = extTool.parameters;
			const cacheKey = `${toolId}:${sql}`;
			const cachedData = queryCache.get(cacheKey);
			const queryId = queryCache.storeQuery(sql, cachedData);
			extTool.parameters = { queryId, ...restParams };
			logger.info(`[DASH_COMP_REQ:FILTER] Replaced SQL with queryId: ${queryId} for component: ${comp.id}`);
		}

		if (skippedComponentIds.length > 0) {
			updatedComponents = updatedComponents.filter((c: any) => !skippedComponentIds.includes(c.id));
			logger.warn(`[DASH_COMP_REQ:FILTER] Dropped ${skippedComponentIds.length} component(s) whose SQL failed validation (kept their pre-filter query): ${skippedComponentIds.join(', ')}`);
		}

		return {
			success: true,
			data: {
				filterComponent: result.filterComponent,
				updatedComponents,
				filterBindings: result.filterBindings || {},
				reasoning: result.reasoning || 'Filter created based on user prompt',
				executedTools: executedTools.length > 0 ? executedTools : undefined,
				skippedComponentIds: skippedComponentIds.length > 0 ? skippedComponentIds : undefined
			},
			errors: []
		};
	} catch (error) {
		const errorMsg = error instanceof Error ? error.message : String(error);
		logger.error(`[DASH_COMP_REQ:FILTER] Error creating filter: ${errorMsg}`);
		userPromptErrorLogger.logError('DASH_COMP_REQ:FILTER', error instanceof Error ? error : new Error(errorMsg), {
			prompt, existingComponentsCount: existingComponents.length
		});
		errors.push(errorMsg);
		return { success: false, errors };
	}
}
